import { useId, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  AppLayout,
  BuildingIcon,
  ClipboardTextIcon,
  DetailsCard,
  HashIcon,
  LabReceptionAddressesSection,
  MapPinAreaIcon,
  Mtds,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  TrashIcon,
  useDeleteLabMutation,
  useGetLabByIdQuery,
  useGetLabReceptionAddressesQuery,
  type ScreenHeaderProperty
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { CopyReferenceValue } from '../tables/reference-table/ReferenceTable'
import { ReferenceDeleteDialog } from '../components/reference-data/ReferenceDeleteDialog'

export const LabScreen = () => {
  const { labId } = useParams<{ labId: string }>()
  const navigate = useNavigate()
  const id = Number(labId)
  const validId =
    /^\d+$/.test(labId ?? '') && Number.isSafeInteger(id) && id > 0
  const {
    data: lab,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useGetLabByIdQuery(validId ? id : undefined)
  const { data: addresses } = useGetLabReceptionAddressesQuery(
    validId ? id : undefined
  )
  const remove = useDeleteLabMutation()
  const contextMenuId = useId()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const headerProperties: ScreenHeaderProperty[] = [
    {
      label: 'ID',
      icon: <HashIcon aria-hidden />,
      value: lab ? <CopyReferenceValue value={lab.id} label='ID' /> : '-'
    },
    {
      label: 'Mottaksadresser',
      icon: <MapPinAreaIcon aria-hidden />,
      value: addresses ? String(addresses.length) : '-'
    }
  ]

  const detailProperties: ScreenHeaderProperty[] = [
    {
      label: 'Informasjon',
      icon: <ClipboardTextIcon aria-hidden />,
      value: lab?.info || 'Ikke angitt'
    },
    {
      label: 'Foretrukket språk',
      icon: <ClipboardTextIcon aria-hidden />,
      value: lab?.preferredLanguage || 'Ikke angitt'
    },
    {
      label: 'Eksterne ID-er',
      icon: <HashIcon aria-hidden />,
      value: lab?.externalIds?.length ? (
        <Mtds.Grid data-gap='1'>
          {lab.externalIds.map((value) => (
            <CopyReferenceValue key={value} value={value} label='Ekstern ID' />
          ))}
        </Mtds.Grid>
      ) : (
        'Ikke angitt'
      )
    }
  ]

  return (
    <AppLayout
      useMobileBar={false}
      isFullWidth
      sidebar={<SideBarLayout />}
      topBar={
        <TopBar
          logo={<AppLogo />}
          breadcrumbItems={[
            { label: 'Laboratorier', to: '/labs' },
            { label: lab?.name ?? 'Laboratorium', to: `/labs/${labId}` }
          ]}
        />
      }
    >
      <ScreenLayout
        mainMaxWidth={1000}
        details={
          lab && (
            <DetailsCard
              title='Detaljer'
              icon={<BuildingIcon aria-hidden />}
              properties={detailProperties}
            />
          )
        }
        header={
          <ScreenHeader
            title={lab?.name ?? 'Laboratorium'}
            properties={headerProperties}
            actions={
              <>
                <Mtds.Button
                  aria-label='Handlingsmeny'
                  disabled={!lab}
                  popoverTarget={contextMenuId}
                />
                <Mtds.Popover as='menu' id={contextMenuId}>
                  <li>
                    <Mtds.Button
                      data-color='danger'
                      popoverTargetAction='hide'
                      onClick={() => {
                        setConfirmDelete(true)
                      }}
                    >
                      <TrashIcon />
                      Slett laboratorium
                    </Mtds.Button>
                  </li>
                </Mtds.Popover>
              </>
            }
          />
        }
      >
        {!validId && (
          <Mtds.Alert data-color='warning'>Ugyldig laboratorie-ID.</Mtds.Alert>
        )}
        {validId && isFetching && (
          <p role='status'>
            {lab
              ? 'Oppdaterer laboratoriet. Viser sist hentede data.'
              : 'Laster laboratorium …'}
          </p>
        )}
        {validId && isError && (
          <Mtds.Alert as='div' role='alert' data-color='danger'>
            <Mtds.Grid>
              <p>
                {lab
                  ? 'Kunne ikke oppdatere laboratoriet. Viser sist hentede data, som kan være utdaterte.'
                  : 'Kunne ikke hente laboratoriet.'}
              </p>
              <Mtds.Button
                data-variant='secondary'
                disabled={isFetching}
                aria-busy={isFetching}
                onClick={() => void refetch()}
              >
                Prøv igjen
              </Mtds.Button>
            </Mtds.Grid>
          </Mtds.Alert>
        )}
        {validId && lab && <LabReceptionAddressesSection labId={lab.id} />}
        {validId && !lab && !isLoading && !isError && (
          <Mtds.Alert data-color='warning'>Fant ikke laboratoriet.</Mtds.Alert>
        )}
      </ScreenLayout>
      {confirmDelete && lab && (
        <ReferenceDeleteDialog
          id='delete-lab-dialog'
          name={lab.name}
          description='Laboratoriet og mottaksadressene blir permanent slettet. Handlingen kan ikke angres.'
          successMessage='Laboratoriet er slettet'
          onClose={() => setConfirmDelete(false)}
          onDelete={() => remove.mutateAsync(lab.id)}
          onDeleted={() => navigate('/labs')}
        />
      )}
    </AppLayout>
  )
}
